<template>
  <v-main>

    <v-container
        id="users-container"
        fluid
        tag="section"
    >

      <base-material-card
          icon="mdi-bank-check"
          class="px-5 py-5"
      >

        <shared-page-messages :error-messages="errorMessages"
                              :warning-messages="warningMessages"
                              :success-messages="successMessages">
        </shared-page-messages>

        <p>If you don't see an invoice in the list after generating invoices, please refresh the page.</p>

          <v-row align="center">
            <v-col
                cols="6"
                lg="2"
                sm="3"
            >
              <v-select
                  v-model="bulkActionStatus"
                  :items="computedBulkInvoiceStatusItems"
                  item-text="text"
                  item-value="value"
                  label="Bulk Status"
                  return-object
                  dense
                  hint="Apply to selected invoices"
                  persistent-hint
              ></v-select>
            </v-col>

            <v-col
                cols="3"
                sm="3"
                lg="2"
            >
              <v-btn
                  small
                  color="primary"
                  @click="handleBulkActionForm"
              >
                Submit
              </v-btn>
            </v-col>
            <v-col
                cols="3"
                lg="8"
                sm="6"
                align="right"
            >

              <v-dialog
                  v-model="filtersDialog"
                  max-width="400"
              >

                <template v-slot:activator="{ on, attrs }">

                  <v-btn
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                  >
                    <v-icon size="small">mdi-filter</v-icon> Filters
                  </v-btn>
                </template>


                <form @submit.prevent="applyFilters">

                  <v-card>
                    <v-card-title class="text-h5">
                      Invoice Filters
                    </v-card-title>

                    <v-card-text>
                      <v-row dense>
                        <v-col
                            cols="12"
                            lg="4"
                        >
                          <v-text-field
                              v-model="filter.invoiceNumber"
                              label="Invoice #"
                              dense
                              :rules="rules.invoiceNum"
                          ></v-text-field>
                        </v-col>

                        <v-col
                            cols="12"
                            lg="8"
                        >
                          <v-select
                              v-model="filter.invoiceStatuses"
                              :items="computedInvoiceStatusItems"
                              label="Invoice Status"
                              return-object
                              dense
                              multiple
                          ></v-select>
                        </v-col>
                      </v-row>


                      <v-row dense>
                        <v-col
                            cols="12"
                        >
                          <v-combobox
                              v-model="filter.claim"
                              label="Claim"
                              placeholder="Start typing to search"
                              :items="computedClaimList"
                              :loading="isLoadingClaims"
                              :search-input.sync="claimSearchString"
                              @input.native="nativeClaimSearchString=$event.srcElement.value"
                              item-text="names"
                              item-value="id"
                              return-object
                              no-filter
                              persistent-placeholder
                              validate-on-blur
                              clearable
                          ></v-combobox>
                        </v-col>
                      </v-row>


                      <v-row dense>
                        <v-col
                            cols="12"
                            lg="6"
                        >
                          <v-text-field
                              v-model="filter.firstName"
                              label="First Name"
                              dense
                          ></v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            lg="6"
                        >
                          <v-text-field
                              v-model="filter.lastName"
                              label="Last Name"
                              dense
                          ></v-text-field>
                        </v-col>
                      </v-row>

                    </v-card-text>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                          small
                          color="primary"
                          @click="filtersDialog = false"
                      >
                        Close
                      </v-btn>
                      <v-btn
                          small
                          color="primary"
                          type="submit"
                      >
                        Apply
                      </v-btn>
                    </v-card-actions>

                  </v-card>

                </form>

              </v-dialog>
            </v-col>
          </v-row>

          <v-simple-table v-if="searchResponse.content" class="pt-5"
            dense
          >
            <template v-slot:default>
              <thead>
              <tr>
                <th class="invoice-action">
                  <v-checkbox
                      v-model="computedSelectAll"
                      hide-details
                      class="shrink ma-0 pa-0"
                  />
                </th>

                <th class="invoice-num text-subtitle-2">Inv. #</th>
                <th class="invoice-bill-to text-subtitle-2">Bill To</th>
                <th class="invoice-amount text-subtitle-2 text-right">Amount</th>
                <th class="invoice-balance text-subtitle-2 text-right">Balance</th>
                <th class="invoice-date text-subtitle-2">Date</th>
                <th class="invoice-due-date text-subtitle-2">Due</th>
                <th class="invoice-status text-subtitle-2">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr
                  v-for="invoice in searchResponse.content"
                  :key="invoice.id"
              >
                <td>
                  <v-checkbox
                      v-model="bulkActionItems"
                      :value="invoice.id"
                      hide-details
                      class="shrink ma-0 pa-0"
                  />
                </td>

                <td>
                  <router-link :to="{ name: 'view_invoice', params: {invoiceId: invoice.id} }">
                    {{ invoice.id }}
                  </router-link>
                </td>
                <td>
                  {{ invoice.user.names }}
                </td>
                <td class="text-right">{{ invoice.total | formatCurrency }}</td>
                <td class="text-right">{{ invoice.balance | formatCurrency }}</td>
                <td>{{ invoice.invoiceDate | formatDate }}</td>
                <td>{{ invoice.dueDate | formatDate }}</td>
                <td>{{ invoiceStatusMap[invoice.status] }}</td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>

        <div class="text-center mt-3">
          <v-pagination
              v-model="filter.page"
              :length="searchResponse.page.totalPages || 1"
              @input="fetchInvoices"
          ></v-pagination>
        </div>


      </base-material-card>


    </v-container>

  </v-main>
</template>

<script>
import api from '@/services/api'
import constants from '@/utils/constants'
import validation from "@/utils/validation";
import SharedPageMessages from '../shared/PageMessages.vue'

export default {
  name: 'CoreInvoicesView',
  components: {SharedPageMessages},

  async created () {
    await this.fetchInvoices()
  },

  watch: {
    claimSearchString(val) {
      if (!this.isLoadingClaims && val !== null && (val.length > 2)) {
        this.fetchClaims(val)
      }
    },
    searchResponse: function (val) {
      if (val.empty) {
        this.warningMessages.push("No invoices matching your filter criteria found. If you just generated invoices, please reload the page.")
      }
    },
    errorMessages: function (val) {
      if (val.length > 0) {
        scroll(0, 0)
      }
    },
    successMessages: function (val) {
      if (val.length > 0) {
        scroll(0, 0)
      }
    },
    warningMessages: function (val) {
      if (val.length > 0) {
        scroll(0, 0)
      }
    }
  },

  computed: {
    computedSelectAll: {
      set: function (value) {
        this.successMessages = ''
        this.bulkActionItems = []
        if (value === true) {
          this.invoices.forEach(invoice => {
            this.bulkActionItems.push(invoice.id)
          })
        }
      },
      get: function () {
        return this.invoices.length > 0 && this.invoices.length === this.bulkActionItems.length
      }
    },
    computedClaimList: {
      get() {
        return this.claims
            .map(claim => ({id: claim.id, names: `${claim.user.names} (${claim.dependent.names})`}))
      }
    },
    computedInvoiceStatusItems: {
      get: function () {
        return Object.keys(this.invoiceStatusMap)
            .map((key) => ({value: key, text: this.invoiceStatusMap[key]}))
      }
    },
    computedBulkInvoiceStatusItems: {
      get: function () {
        return Object.keys(this.invoiceStatusMap)
            .filter(key => key !== 'PAID')
            .map((key) => ({value: key, text: this.invoiceStatusMap[key]}))
      }
    }
  },

  methods: {
    clearMessages() {
      this.successMessages = []
      this.errorMessages = []
      this.warningMessages = []
    },
    handleBulkActionForm() {
      this.clearMessages()
      if (this.bulkActionStatus === null) {
        this.errorMessages.push('Please select a bulk status')
      }

      if (this.bulkActionItems.length === 0) {
        this.errorMessages.push('Please select at least one invoice')
      }

      if (this.errorMessages.length === 0) {
        this.processBulkAction()
      }
    },

    applyFilters() {
      this.filtersDialog = false
      this.clearMessages()
      this.fetchInvoices()
    },

    async fetchClaims(val) {
      this.clearMessages()
      this.isLoadingClaims = true
      let nameParts = val.split(' ')
      let firstPart = nameParts.shift()
      let lastPart = nameParts.pop() || firstPart
      let filters = {
        firstName: firstPart,
        lastName: lastPart
      }
      return api.searchClaims(filters)
          .then(response => {
            this.claims = response.data.content
          })
          .catch(err => {
            this.handleError(err)
          })
          .finally(() => this.isLoadingClaims = false)
    },

    async fetchInvoices(page) {
      this.isLoadingInvoices = true
      this.filter.page = page || 1
      const claimId = this.filter.claim ? this.filter.claim.id : null
      let filterParams = {
        invoiceNumbers: this.filter.invoiceNumber,
        claimId: claimId,
        statuses: this.filter.invoiceStatuses.map(s => s.value).join(','),
        firstName: this.filter.firstName,
        lastName: this.filter.lastName,
        limit: this.filter.itemsPerPage,
        page: this.filter.page,
      }
      return api
        .fetchInvoices(filterParams)
        .then(response => {
          this.searchResponse = response.data
        })
        .catch(err => {
          this.handleError(err)
        })
        .finally(() => this.isLoadingInvoices = false)
    },

    async processBulkAction() {
      this.clearMessages()
      let payload = {
        status: this.bulkActionStatus.value,
        ids: this.bulkActionItems
      }

      return api.updateBulkInvoiceStatuses(payload)
          .then(() => {
            this.bulkActionItems = []
            this.bulkActionStatus = null
            this.fetchInvoices()
            this.successMessages.push('Invoices updated successfully.')
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    handleError(err) {
      let errorMessage = api.parseResponseError(err, true)
      this.errorMessages.push(errorMessage)
    }
  },

  data() {
    return {
      valid: true,
      errorMessages: [],
      warningMessages: [],
      successMessages: [],
      invoices: [],
      claims: [],
      bulkActionStatus: null,
      bulkActionItems: [],
      bulkActionValid: false,
      filterInvoiceStatus: null,
      filtersDialog: false,

      selectedClaims: [],
      claimSearchString: '',
      nativeClaimSearchString: '',
      isLoadingClaims: false,
      isLoadingInvoices: false,

      paymentMethodMap: constants.getPaymentMethodMap(),
      filter: {
        invoiceStatuses: [],
        claim: null,
        page: 1,
        itemsPerPage: 25,
      },
      searchResponse: {
        content: [],
        empty: true,
        page: {
          totalPages: 0,
        }
      },
      invoiceStatusMap: constants.getInvoiceStatusMap(),
      invoiceTypeMap: constants.getInvoiceTypeMap(),
      rules: {
        invoiceNum: [
            v => validation.isOptionalInteger(v) || 'must be a number'
        ]
      }
    }
  },
}
</script>

<style lang="sass">
  th.invoice-action, th.invoice-amount, th.invoice-balance, th.invoice-date, th.invoice-due-date, th.invoice-status
    width: 5%

  th.invoice-num
    width: 10%

  .v-btn
    margin-right: 10px
    margin-left: 10px


</style>
